import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import '../../style/scss/sms.scss';

// A purchasable prepaid bundle, used for sms credits and subcontractor day passes
export interface CreditBundle {
    key: string;
    quantity: number;
    unit_price: string;
    subtotal: string;
    transaction_fee: string;
    total_ex_vat: string;
    vat_amount: string;
    total_inc_vat: string;
}

interface CreditBundleOrderModalProps {
    bundles: CreditBundle[];
    orderApiUrl: string; // The order endpoint without the environment hash, the hash is appended
    returnPath: string; // The thank you page the bank redirects to after the ideal payment
    translationPrefix: string; // The translation block with the modal texts, for example sms.credits
    onOrderCompleted?: () => void;
}

// The modal to order a prepaid bundle, paid with ideal. One modal serves the sms
// credits and the subcontractor day passes so both flows look and work the same
const CreditBundleOrderModal: React.FC<CreditBundleOrderModalProps> = ({ bundles, orderApiUrl, returnPath, translationPrefix, onOrderCompleted }) => {
    const { t } = useTranslation();
    const stripe = useStripe();
    const { environmentHash } = useAuthContext();
    const { revealModal, closeModal } = useModal();
    const [selectedBundle, setSelectedBundle] = useState<string | null>(null);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reveal the modal on the first render
    useEffect(() => {
        revealModal();
    }, []);

    // Start the ideal payment for the selected bundle
    const handleOrder = async () => {
        if (!selectedBundle || !stripe) return;
        setButtonLoader(true);
        setError(null);

        try {
            // Create the pending order and the payment intent
            const response = await saveData({
                apiUrl: `${orderApiUrl}/${environmentHash}`,
                method: 'post',
                data: { bundle_key: selectedBundle }
            });

            // Show an error when the order could not be created
            if (response?.status !== 200) {
                setError(t(`${translationPrefix}.order_failed_error`));
                return;
            }

            // Confirm the ideal payment, this redirects to the bank
            await stripe.confirmIdealPayment(response.data.client_secret, {
                payment_method: { ideal: {} },
                return_url: `${window.location.origin}${returnPath}`
            });
        } catch (requestError: any) {
            console.error(requestError);

            // Show the reason of the backend when it gives one, for example a trial without subscription
            const backendMessage = requestError?.response?.data?.general;
            setError(backendMessage ? t(backendMessage) : t(`${translationPrefix}.order_failed_error`));
        } finally {
            setButtonLoader(false);
        }
    };

    return (
        <div className='sms-credit-order-modal'>
            <h3>{t(`${translationPrefix}.order_modal_title`)}</h3>
            <p>{t(`${translationPrefix}.order_modal_description`)}</p>

            {error && (
                <div className='alert form-alert alert-danger' role='alert'>{error}</div>
            )}

            {/* The three bundle cards next to each other */}
            <div className='sms-bundle-cards'>
                {bundles.map(bundle => (
                    <div
                        key={bundle.key}
                        className={`sms-bundle-card ${selectedBundle === bundle.key ? 'selected' : ''}`}
                        onClick={() => setSelectedBundle(bundle.key)}
                    >
                        <h4>{bundle.quantity} {t(`${translationPrefix}.bundle_credits_label`)}</h4>
                        <p className='sms-bundle-unit-price'>
                            {t(`${translationPrefix}.bundle_unit_price`, { price: bundle.unit_price.replace('.', ',') })}
                        </p>
                        <p className='sms-bundle-total'>
                            &euro; {bundle.subtotal.replace('.', ',')}
                        </p>
                        <p className='helper-text'>{t(`${translationPrefix}.bundle_total_ex_vat_label`)}</p>
                    </div>
                ))}
            </div>

            <p className='helper-text'>{t(`${translationPrefix}.transaction_fee_explanation`)}</p>

            {/* The modal buttons */}
            <div className='button-row'>
                <div className='buttons-right'>
                    <SecondaryButton
                        type='button'
                        label='general.cancel'
                        size='small'
                        onClick={() => closeModal()}
                    />
                    <PrimaryButton
                        type='button'
                        label={`${translationPrefix}.pay_with_ideal_button`}
                        size='small'
                        loading={buttonLoader}
                        disabled={{ disabled: !selectedBundle, message: t(`${translationPrefix}.select_bundle_first`) }}
                        onClick={handleOrder}
                    />
                </div>
            </div>
        </div>
    );
};

export default CreditBundleOrderModal;
